import {
  App as F7App,
  Link,
  LoginScreen,
  Page,
  Tab,
  Tabs,
  Toolbar,
  View,
  Views,
  f7ready,
} from 'framework7-react';
import { ComponentProps, FC, ReactElement, memo, useEffect } from 'react';

import { queryClient } from '@contexts/queryClient';
import { jwtDecode } from 'jwt-decode';

import { fetchCurrentEntertainer, useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { fetchCurrentPromoter } from '@queries/PromoterQueries';

import { AppStorageKey } from '@utilities/appStorage';

import { ChatScreen } from '@screens/ChatScreen/ChatScreen';
import { EntertainerCommunityScreen } from '@screens/CommunityScreen/CommunityScreen';
import { DashboardScreen } from '@screens/CommunityScreen/DashboardScreen';
import { EntertainerDashboardScreen } from '@screens/CommunityScreen/EntertainerDashboardScreen';
// import { CommunityScreen } from '@screens/CommunityScreen/CommunityScreen';
import { ConversationsScreen } from '@screens/ConversationsScreen/ConversationsScreen';
import { CreateEditBookingOpportunityEventScreen } from '@screens/CreateEditEventScreen/CreateEditBookingOpportunityEventScreen';
import { CreateEditEntertainerEventScreen } from '@screens/CreateEditEventScreen/CreateEditEntertainerEventScreen';
import { CreateEditPromoterEventScreen } from '@screens/CreateEditEventScreen/CreateEditPromoterEventScreen';
import { CritiqueScreen } from '@screens/CritiqueScreen/CritiqueScreen';
import { DiscoverScreen } from '@screens/DiscoverScreen/DiscoverScreen';
import { EditProfileScreen } from '@screens/EditProfileScreen';
import { EventCleanupScreen } from '@screens/EventCleanupScreen';
import { EventsScreen } from '@screens/EventsScreen';
import { HomeScreen } from '@screens/HomeScreen/HomeScreen';
import { InterestScreen } from '@screens/InterestScreen/InterestScreen';
import { InterestsScreen } from '@screens/InterestsScreen/InterestsScreen';
import { LogInScreen } from '@screens/LogInScreen/LogInScreen';
import { MagicLinkScreen } from '@screens/MagicLinkScreen';
import { NotificationsScreen } from '@screens/NotificationsScreen/NotificationsScreen';
import { ProfileScreen } from '@screens/ProfileScreen';
import { PromoterScreen } from '@screens/PromoterScreen';
import { QueryScreen } from '@screens/QueryScreen/QueryScreen';
import { QuickPicksScreen } from '@screens/QuickPicksScreen/QuickPicksScreen';
import { SearchScreen } from '@screens/SearchScreen/SearchScreen';
import { SettingsScreen } from '@screens/SettingsScreen';
import { SmsResponseScreen } from '@screens/SmsResponseScreen';
import { SmsScreen } from '@screens/SmsScreen';
import { SubmitVenueScreen } from '@screens/SubmitVenueScreen';
import { SuggestedEventsScreen } from '@screens/SuggestedEventsScreen/SuggestedEventsScreen';
import { TabScreen } from '@screens/TabScreen';
import { ManageSentBookingRequestScreen } from '@screens/TalentOrganizerScreen/ManageSentBookingRequestScreen';
import { TalentOrganizerScreen } from '@screens/TalentOrganizerScreen/TalentOrganizerScreen';
import { TriviaScreen } from '@screens/TriviaScreen/TriviaScreen';
import { BookingWizardScreen } from '@screens/booking/BookingWizardScreen/BookingWizardScreen';
import { GigsAndTalentScreen } from '@screens/booking/GigsAndTalentScreen/GigsAndTalentScreen';
import { ChallengesScreen } from '@screens/challenge/ChallengesScreen';
import { ConnectionsScreen } from '@screens/connnections/ConnectionsScreen/ConnectionsScreen';
import { SuggestedConnectionsScreen } from '@screens/connnections/SuggestedConnectionsScreen/SuggestedConnectionsScreen';
import { UserConnectionComparisonScreen } from '@screens/connnections/UserConnectionComparisonScreen';
import { BookingRequestScreen } from '@screens/entertainer/BookingRequestScreen';
import { CityEntertainerRecommendationGroupsScreen } from '@screens/entertainer/CityEntertainerStandoutsScreen/CityEntertainerRecommendationGroupsScreen';
import { CityEntertainerStandoutsScreen } from '@screens/entertainer/CityEntertainerStandoutsScreen/CityEntertainerStandoutsScreen';
import { PersonalizedBadgeScreen } from '@screens/entertainer/CityEntertainerStandoutsScreen/PersonalizedBadgeScreen';
import { TopKingMaterialScreen } from '@screens/entertainer/CityEntertainerStandoutsScreen/TopKingMaterialScreen';
import { DesignPartnerScreen } from '@screens/entertainer/DesignPartnerScreen/DesignPartnerScreen';
import { CityDragShowManagementScreen } from '@screens/entertainer/DragKingCommunityScreen/CityDragShowManagementScreen';
import { DragKingCommunityScreen } from '@screens/entertainer/DragKingCommunityScreen/DragKingCommunityScreen';
import { DragKingShoutoutsScreen } from '@screens/entertainer/DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { MostBookedEntertainersScreen } from '@screens/entertainer/DragKingCommunityScreen/MostBookedEntertainersScreen';
import { EditEntertainerProfileScreen } from '@screens/entertainer/EditEntertainerProfileScreen';
import { ElectionCampaignScreen } from '@screens/entertainer/ElectionCampaignScreen/ElectionCampaignScreen';
import { EntertainerBadgesScreen } from '@screens/entertainer/EntertainerBadgesScreen/EntertainerBadgesScreen';
import { EntertainerCompetitionScreen } from '@screens/entertainer/EntertainerCompetitionScreen/EntertainerCompetitionScreen';
import {
  EntertainerDetailsScreen,
  ManageEntertainerProfileDetailsScreen,
} from '@screens/entertainer/EntertainerDetailsScreen/EntertainerDetailsScreen';
import { EntertainerLooksSwiperScreen } from '@screens/entertainer/EntertainerLooksSwiperScreen/EntertainerLooksSwiperScreen';
import { PersonalizedBadgeSwiperScreen } from '@screens/entertainer/EntertainerLooksSwiperScreen/PersonalizedBadgeSwiperScreen';
import { EntertainerMetricsScreen } from '@screens/entertainer/EntertainerMetricsScreen/EntertainerMetricsScreen';
import { CityEngagementStandingsScreen } from '@screens/entertainer/EntertainerRankingsScreen/CityEngagementStandingsScreen';
import { CityEntertainerRatingScreen } from '@screens/entertainer/EntertainerRankingsScreen/CityEntertainerRatingScreen';
import { EntertainerRankingsScreen } from '@screens/entertainer/EntertainerRankingsScreen/EntertainerRankingsScreen';
import { EntertainerSweepstakesScreen } from '@screens/entertainer/EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import { EventRequestsScreen } from '@screens/entertainer/EventRequestsScreen/EventRequestsScreen';
import { FeaturedLooksScreen } from '@screens/entertainer/FeaturedLooksScreen/FeaturedLooksScreen';
import { LandingPageCampaignScreen } from '@screens/entertainer/LandingPageCampaignScreen/LandingPageCampaignScreen';
import { ReferralCampaignScreen } from '@screens/entertainer/LandingPageCampaignScreen/ReferralCampaignScreen';
import {
  ManageEntertainerBookingOpportunityScreen,
  ManageNoauthBookingOpportunityScreen,
} from '@screens/entertainer/ManageEntertainerBookingOpportunityScreen/ManageEntertainerBookingOpportunityScreen';
import { ManageEntertainerBookingRequestScreen } from '@screens/entertainer/ManageEntertainerBookingRequestScreen/ManageEntertainerBookingRequestScreen';
import { ManageEntertainerProfileScreen } from '@screens/entertainer/ManageEntertainerProfileScreen';
import { ManageEntertainerScheduleScreen } from '@screens/entertainer/ManageEntertainerScheduleScreen';
import { OnboardEntertainerScreen } from '@screens/entertainer/OnboardEntertainerScreen/OnboardEntertainerScreen';
import { ShareFeaturedLookCollabPostScreen } from '@screens/entertainer/ShareFeaturedLookCollabPostScreen';
import { RecommendEntertainersScreen } from '@screens/entertainer/SupportEntertainersScreen/RecommendEntertainersScreen';
import { SupportEntertainersScreen } from '@screens/entertainer/SupportEntertainersScreen/SupportEntertainersScreen';
import { EventLineupReferralInstructionsScreen } from '@screens/event/EventLineupReferralInstructionsScreen';
import { EventRsvpScreen } from '@screens/event/EventRsvpScreen/EventRsvpScreen';
import { EventCompetitionReviewsScreen } from '@screens/event/EventScreen/EventCompetitionReviewsScreen';
import { EventReviewsScreen } from '@screens/event/EventScreen/EventReviewsScreen';
import { EventScreen } from '@screens/event/EventScreen/EventScreen';
import { ConnectInstagramScreen } from '@screens/instagram/ConnectInstagramScreen';
import { NoAuthDownloadScreen } from '@screens/noauth/NoAuthDownloadScreen/NoAuthDownloadScreen';
import { NoAuthQuickPickScreen } from '@screens/noauth/NoAuthQuickPickScreen/NoAuthQuickPickScreen';
import { OnboardingBasicInfoScreen } from '@screens/onboarding/OnboardingBasicInfoScreen/OnboardingBasicInfoScreen';
import { OnboardingCritiquesScreen } from '@screens/onboarding/OnboardingCritiquesScreen/OnboardingCritiquesScreen';
import { OnboardingInterestsScreen } from '@screens/onboarding/OnboardingInterestsScreen/OnboardingInterestsScreen';
import { OnboardingQuickPicksScreen } from '@screens/onboarding/OnboardingQuickPicksScreen/OnboardingQuickPicksScreen';
import { OnboardingUserTypeScreen } from '@screens/onboarding/OnboardingUserTypeScreen';
import { CreateEditBookingOpportunitesScreen } from '@screens/promoter/CreateEditBookingOpportunityScreen/CreateEditBookingOpportunityScreen';
import { EditPromoterProfileScreen } from '@screens/promoter/EditPromoterProfileScreen/EditPromoterProfileScreen';
import { ManageCompetitionsScreen } from '@screens/promoter/ManageCompetitionsScreen';
import { ManagePromoterBookingOpportunityScreen } from '@screens/promoter/ManagePromoterBookingOpportunityScreen/ManagePromoterBookingOpportunityScreen';
import {
  ManagePromoterProfileScreen,
  PromoterDetailsScreen,
} from '@screens/promoter/ManagePromoterProfileScreen/ManagePromoterProfileScreen';
import { OnboardPromoterScreen } from '@screens/promoter/OnboardPromoterScreen/OnboardPromoterScreen';
import { PromotersScreen } from '@screens/promoter/PromotersScreen';
import { ManageUserScheduleSubmissionsScreen } from '@screens/schedule-submissions/ManageUserScheduleSubmissionsScreen';
import { SeriesAddRsvpEventScreen } from '@screens/series/SeriesAddRsvpEventScreen/SeriesAddRsvpEventScreen';
import { SeriesEventsScreen } from '@screens/series/SeriesEventsScreen/SeriesEventsScreen';
import { SeriesFeatureFormScreen } from '@screens/series/SeriesFeatureFormScreen/SeriesFeatureFormScreen';
import { SeriesFilterPlannerScreen } from '@screens/series/SeriesFilterPlannerScreen';
import { SeriesPlannerScreen } from '@screens/series/SeriesPlannerScreen';
import { BonusTipWeekendScreen } from '@screens/series/SeriesScreen/BonusTipWeekendScreen';
import { ContributeAndEarnScreen } from '@screens/series/SeriesScreen/ContributeAndEarnScreen';
import { SeriesScreen } from '@screens/series/SeriesScreen/SeriesScreen';
import { SeriesShowsScreen } from '@screens/series/SeriesScreen/SeriesShowsScreen';
import { SocialCircleInquiryScreen } from '@screens/social-circles/SocialCircleInquiryScreen/SocialCircleInquiryScreen';
import { SocialCirclesScreen } from '@screens/social-circles/SocialCirclesScreen/SocialCirclesScreen';
import { ManageTriviaChallengeScreen } from '@screens/super-admin/ManageTriviaChallengeScreen';
import { SuperAdminManageBookingOpportunityScreen } from '@screens/super-admin/SuperAdminManageBookingOpportunityScreen';
import { SuperAdminManageCDGQuotesScreen } from '@screens/super-admin/SuperAdminManageCDGQuotesScreen';
import { SuperAdminManageUpcomingEventsScreen } from '@screens/super-admin/SuperAdminManageUpcomingEventsScreen';
import { SuperAdminManageWeeklyTriviaScreen } from '@screens/super-admin/SuperAdminManageWeeklyTriviaScreen';
import { SuperAdminPendingEventsScreen } from '@screens/super-admin/SuperAdminPendingEventsScreen';
import { SuperAdminUserPayoutRequestsScreen } from '@screens/super-admin/SuperAdminUserPayoutRequestsScreen';
import { VenueRankingsScreen } from '@screens/venue/VenueRankingsScreen/VenueRankingsScreen';
import { VenueScreen } from '@screens/venue/VenueScreen/VenueScreen';
import { VenuesScreen } from '@screens/venue/VenuesScreen';
import { WorldPrideFormsScreen } from '@screens/world-pride/WorldPrideFormsScreen/WorldPrideFormsScreen';
import { WorldPrideShowManagementScreen } from '@screens/world-pride/WorldPrideShowManagementScreen/WorldPrideShowManagementScreen';

import { useLocation, useNavigate, withTabbar } from '.';
import { IS_PRODUCER_ENABLED } from '../constants/FeatureFlags';
import { CoreLayout } from '../layouts/CoreLayout';
import { NoAuthCreateEditBookingOpportunityScreen } from '../screens/promoter/CreateEditBookingOpportunityScreen/NoAuthCreateBookingOpportunityScreen';

const MockTabs = () => {
  useEffect(() => {
    f7ready((f7) => {
      console.log(f7.tab, f7);
    });
  });

  return (
    <Page pageContent={false}>
      <Toolbar tabbar bottom>
        <Link href="/" tabLink="tab-1" text="Tab 1" />
        <Link href="/tab-2/" tabLink="tab-2" text="Tab 2" />
        <Link href="/tab-3/" tabLink="tab-3" text="Tab 3" />
      </Toolbar>

      <Tabs routable>
        <Tab id="tab-1">Tab 1</Tab>
        <Tab id="tab-2">Tab 2</Tab>
        <Tab id="tab-3">Tab 3</Tab>
      </Tabs>
    </Page>
  );
};

export const f7routes: ComponentProps<typeof F7App>['routes'] = [
  // {
  //   path: '/tabs/',
  //   component: MockTabs,
  //   tabs: [
  //     {
  //       path: '/',
  //       id: 'tab-1',
  //       content: `
  //         <div className="block">
  //           <h3>Tab 1</h3>
  //           <p>...</p>
  //         </div>
  //       `,
  //     },
  //     {
  //       path: '/tab-2/',
  //       id: 'tab-2',
  //       content: `
  //         <div className="block">
  //           <h3>Tab 2</h3>
  //           <p>...</p>
  //         </div>
  //       `,
  //     },
  //     {
  //       path: '/tab-3/',
  //       id: 'tab-3',
  //       content: `
  //         <div className="block">
  //           <h3>Tab 3</h3>
  //           <p>...</p>
  //         </div>
  //       `,
  //     },
  //   ],
  // },
  {
    path: '/',
    async: async ({ resolve }) => {
      const token = localStorage.getItem('token');

      if (token) {
        // Do this in a reuseable away
        const decoded = jwtDecode<{ entertainerId?: string }>(token);

        if (decoded?.entertainerId) {
          return resolve({
            component: (props) => (
              <EntertainerDashboardScreen key="root-entertainer-dashboard" {...props} />
            ),
          });
        }

        return resolve({
          component: (props) => <DashboardScreen key="root-dashboard" {...props} />,
        });
      }

      return resolve({ component: (props) => <LogInScreen key="root-login" {...props} /> });
    },
  },
  {
    path: '/login',
    component: (props) => <LogInScreen key="login-route" {...props} />,
  },
  {
    path: '/magic-link/entertainer',
    async: async ({ resolve, reject }) => {
      try {
        const { entertainer } = await fetchCurrentEntertainer(queryClient);
        if (entertainer?.title) {
          return resolve({ component: ManageEntertainerProfileScreen });
        }
        return resolve({ component: OnboardEntertainerScreen });
      } catch (e) {
        reject();
      }
    },
  },
  {
    path: '/magic-link/promoter',
    async: async ({ resolve, reject }) => {
      try {
        const { promoter } = await fetchCurrentPromoter(queryClient);
        if (promoter.title) {
          return resolve({ component: ManagePromoterProfileScreen });
        }
        return resolve({ component: OnboardPromoterScreen });
      } catch (e) {
        reject();
      }
    },
  },
  {
    path: '/magic-link/direct-message',
    redirect: ({ to, resolve }) => {
      const { currentUserId, entertainerId } = to?.query ?? {};

      if (currentUserId && entertainerId) {
        return resolve(`/chat/user/${currentUserId}/entertainer/${entertainerId}`);
      }

      return resolve('/');
    },
  },
  {
    path: '/magic-link/challenges',
    redirect: ({ resolve }) => {
      return resolve('/challenges');
    },
  },
  {
    path: '/magic-link/url',
    redirect: ({ to, resolve }) => {
      const { url } = to?.query ?? {};

      console.log('url redirect', { url });

      if (url) {
        return resolve(url);
      }

      return resolve('/');
    },
  },
  {
    path: '/magic-link',
    component: MagicLinkScreen,
  },
  {
    path: '/home',
    component: (props) => <DashboardScreen key="home-dashboard" {...props} />,
  },
  {
    path: '/discover',
    component: DiscoverScreen,
  },
  {
    path: '/discover/:queryId',
    component: QueryScreen,
  },
  {
    path: '/search',
    component: SearchScreen,
  },
  { path: '/interests', component: InterestsScreen },
  { path: '/interests/:tagId', component: InterestScreen },
  {
    path: '/community',
    component: (props) => <DashboardScreen key="community-dashboard" {...props} />,
  },
  {
    path: '/deprecated/community',
    component: (props) => <EntertainerCommunityScreen {...props} />,
  },
  {
    path: '/community/:tab',
    component: (props) => (
      <DashboardScreen key={`community-dashboard-${props.tab ?? Math.random()}`} {...props} />
    ),
  },
  {
    path: '/challenges',
    // component: ChallengesScreen,
    redirect: ({ resolve }) => {
      return resolve('/');
    },
  },
  {
    path: '/noauth/challenges/collab-post',
    component: ShareFeaturedLookCollabPostScreen,
  },
  { path: '/notifications', component: NotificationsScreen },
  {
    path: '/events',
    component: EventsScreen,
  },
  {
    path: '/events/suggested',
    component: SuggestedEventsScreen,
  },
  {
    path: '/events/:eventId',
    component: EventScreen,
  },
  {
    path: '/events/:eventId/rsvp',
    component: EventRsvpScreen,
  },
  {
    path: '/events/:eventId/reviews',
    component: EventReviewsScreen,
  },
  {
    path: '/events/:eventId/competition-reviews',
    component: EventCompetitionReviewsScreen,
  },
  {
    path: '/events/:eventId/cleanup',
    component: EventCleanupScreen,
  },
  {
    path: '/venues',
    component: VenuesScreen,
  },
  {
    path: '/venues/request',
    component: SubmitVenueScreen,
  },
  {
    path: '/venues/:venueId',
    component: VenueScreen,
  },
  {
    path: '/promoters',
    component: PromotersScreen,
  },

  {
    path: '/promoters/:promoterId',
    component: PromoterScreen,
  },
  {
    path: '/profile',
    component: ProfileScreen,
  },
  {
    path: '/profile/edit',
    component: EditProfileScreen,
  },
  {
    path: '/profile/settings',
    component: SettingsScreen,
  },
  {
    path: '/noauth/manage/admin/shows',
    component: CityDragShowManagementScreen,
  },
  {
    path: '/manage/promoter',
    component: ManagePromoterProfileScreen,
  },
  {
    path: '/manage/promoter/edit',
    component: EditPromoterProfileScreen,
  },
  {
    path: '/manage/promoter/events/create',
    component: CreateEditPromoterEventScreen,
  },
  {
    path: '/manage/promoter/events/:eventId',
    component: CreateEditPromoterEventScreen,
  },
  {
    path: '/manage/talent',
    component: TalentOrganizerScreen,
  },
  { path: '/manage/connections', component: ConnectionsScreen },
  { path: '/manage/connections/suggested', component: SuggestedConnectionsScreen },
  { path: '/noauth/ucc/:otherUserId', component: UserConnectionComparisonScreen },
  { path: '/manage/circles', component: SocialCirclesScreen },
  {
    path: '/noauth/social-circles/:socialCircleId',
    // SocialCircleScreen
    component: () => {},
  },
  {
    path: '/noauth/social-circle-inquiries/:socialCircleInquiryId',
    component: SocialCircleInquiryScreen,
  },
  {
    path: '/manage/talent/booking-request/:bookingRequestId',
    component: ManageSentBookingRequestScreen,
  },
  {
    path: '/open-bookings/create-edit',
    component: CreateEditBookingOpportunitesScreen,
  },
  {
    path: '/noauth/open-bookings/:cityId/create',
    component: NoAuthCreateEditBookingOpportunityScreen,
  },
  {
    path: '/manage/promoter/open-bookings/:bookingOpportunityId',
    component: ManagePromoterBookingOpportunityScreen,
  },
  {
    path: '/manage/promoter/booking-opportunities/:bookingOpportunityId/event',
    component: CreateEditBookingOpportunityEventScreen,
  },
  {
    path: '/manage/competitions',
    component: ManageCompetitionsScreen,
  },
  {
    path: '/manage/entertainer',
    component: ManageEntertainerProfileDetailsScreen,
  },
  {
    path: '/manage/entertainer/badges',
    component: EntertainerBadgesScreen,
  },
  {
    path: '/manage/entertainer/edit',
    component: EditEntertainerProfileScreen,
  },
  {
    path: '/manage/entertainer/events/create',
    component: CreateEditEntertainerEventScreen,
  },
  {
    path: '/manage/entertainer/events/:eventId',
    component: CreateEditEntertainerEventScreen,
  },
  {
    path: '/manage/entertainer/event-requests',
    component: EventRequestsScreen,
  },
  {
    path: '/manage/entertainer/schedule',
    component: IS_PRODUCER_ENABLED
      ? ManageUserScheduleSubmissionsScreen
      : ManageEntertainerScheduleScreen,
  },
  {
    path: '/manage/schedule-submissions',
    component: ManageUserScheduleSubmissionsScreen,
  },
  {
    path: '/manage/entertainer/metrics',
    component: EntertainerMetricsScreen,
  },
  {
    path: '/manage/entertainer/booking-opportunity/:bookingOpportunityId',
    component: ManageEntertainerBookingOpportunityScreen,
  },
  {
    path: '/manage/entertainer/booking-request/:bookingRequestId',
    component: ManageEntertainerBookingRequestScreen,
  },
  {
    path: '/manage/entertainer/competition/:campaignId',
    component: EntertainerCompetitionScreen,
  },
  {
    path: '/entertainer/onboarding',
    component: OnboardEntertainerScreen,
  },
  {
    path: '/noauth/entertainer/onboarding',
    component: OnboardEntertainerScreen,
  },
  {
    path: '/noauth/featured-looks',
    component: FeaturedLooksScreen,
  },
  {
    path: '/noauth/election-results-campaign',
    component: ElectionCampaignScreen,
  },
  {
    path: '/noauth/landing-page-campaign',
    component: LandingPageCampaignScreen,
  },
  {
    path: '/noauth/design-partner',
    component: DesignPartnerScreen,
  },
  {
    path: '/noauth/referral-campaign',
    component: ReferralCampaignScreen,
  },
  {
    path: '/noauth/booking-wizard',
    component: BookingWizardScreen,
  },
  {
    path: '/noauth/world-pride',
    component: WorldPrideFormsScreen,
  },
  {
    path: '/noauth/world-pride/manage-shows',
    component: WorldPrideShowManagementScreen,
  },
  {
    path: '/noauth/top-king-material',
    component: TopKingMaterialScreen,
  },
  {
    path: '/noauth/booking-wizard/:type',
    component: BookingWizardScreen,
  },
  {
    path: '/noauth/gigs-and-talent/:cityId',
    component: GigsAndTalentScreen,
  },
  {
    path: '/gigs-and-talent',
    component: GigsAndTalentScreen,
  },
  {
    path: '/engagement/looks',
    component: EntertainerLooksSwiperScreen,
  },
  {
    path: '/engagement/personalized-badge/:badgeId',
    component: PersonalizedBadgeSwiperScreen,
  },
  {
    path: '/noauth/personalized-badge/:badgeId',
    component: PersonalizedBadgeScreen,
  },
  {
    path: '/engagement/support',
    component: RecommendEntertainersScreen,
  },
  {
    path: '/entertainers/support',
    component: SupportEntertainersScreen,
  },
  {
    path: '/entertainer/:bookingHandle',
    component: OnboardEntertainerScreen,
  },
  {
    path: '/noauth/entertainer/:bookingHandle',
    component: EntertainerDetailsScreen,
  },
  {
    path: '/noauth/promoter/:bookingHandle',
    component: PromoterDetailsScreen,
  },
  {
    path: '/noauth/entertainer-recommendations',
    component: CityEntertainerRecommendationGroupsScreen,
  },
  {
    path: '/noauth/entertainers/:type/:cityId/standouts',
    // component: EntertainerRankingsScreen,
    component: CityEntertainerStandoutsScreen,
  },
  {
    path: '/noauth/entertainers/:type/:cityId/rankings',
    // component: EntertainerRankingsScreen,
    component: CityEntertainerRatingScreen,
  },
  {
    path: '/noauth/engagement-standings',
    // component: EntertainerRankingsScreen,
    component: CityEngagementStandingsScreen,
  },
  {
    path: '/noauth/entertainer-sweepstakes',
    component: EntertainerSweepstakesScreen,
  },
  {
    path: '/noauth/drag-king-community',
    component: DragKingCommunityScreen,
  },
  { path: '/noauth/connect-instagram', component: ConnectInstagramScreen },
  {
    path: '/noauth/drag-king-shoutouts',
    component: DragKingShoutoutsScreen,
  },
  {
    path: '/noauth/drag-king-shoutouts/:cityId/most-booked',
    component: MostBookedEntertainersScreen,
  },
  {
    path: '/noauth/cities/:cityId/venues/rankings',
    component: VenueRankingsScreen,
  },
  { path: '/noauth/book/:bookingHandle', component: BookingRequestScreen },
  { path: '/noauth/series/:tagSlug', component: SeriesScreen },
  { path: '/noauth/series/:tagSlug/events', component: SeriesEventsScreen },
  { path: '/noauth/series/:tagSlug/bonus-tip-weekend', component: BonusTipWeekendScreen },
  { path: '/noauth/series/:tagSlug/support-and-earn', component: ContributeAndEarnScreen },
  { path: '/noauth/series/:tagSlug/shows', component: SeriesShowsScreen },
  { path: '/noauth/series/:tagSlug/apply', component: SeriesFeatureFormScreen },
  { path: '/noauth/series/:tagSlug/add-rsvp-event', component: SeriesAddRsvpEventScreen },
  {
    path: '/noauth/series/:tagSlug/planner/:startDate/:endDate',
    component: SeriesPlannerScreen,
  },
  {
    path: '/noauth/series/:tagSlug/filter-planner/:filter',
    component: SeriesFilterPlannerScreen,
  },
  {
    path: '/noauth/series/:tagSlug/lineup-referral',
    component: EventLineupReferralInstructionsScreen,
  },
  {
    path: '/response-to/:incomingMessageId',
    component: SmsResponseScreen,
  },
  {
    path: '/sms',
    component: SmsScreen,
  },
  {
    path: '/super-admin/manage/events',
    component: SuperAdminManageUpcomingEventsScreen,
  },
  {
    path: '/super-admin/manage/pending-events',
    component: SuperAdminPendingEventsScreen,
  },
  {
    path: '/super-admin/manage/trivia',
    component: SuperAdminManageWeeklyTriviaScreen,
  },
  {
    path: '/super-admin/manage/trivia/:triviaChallengeId',
    component: ManageTriviaChallengeScreen,
  },
  {
    path: '/super-admin/booking-opportunities/:bookingOpportunityId',
    component: SuperAdminManageBookingOpportunityScreen,
  },
  {
    path: '/super-admin/manage/cdg-quotes',
    component: SuperAdminManageCDGQuotesScreen,
  },
  {
    path: '/super-admin/user-payout-requests',
    component: SuperAdminUserPayoutRequestsScreen,
  },
  {
    path: '/trivia',
    component: TriviaScreen,
  },
  {
    path: '/onboarding',
    component: OnboardingUserTypeScreen,
  },
  {
    path: '/onboarding/basic-info',
    component: OnboardingBasicInfoScreen,
  },
  {
    path: '/onboarding/interests',
    component: OnboardingInterestsScreen,
  },
  {
    path: '/onboarding/critiques',
    component: OnboardingCritiquesScreen,
  },
  {
    path: '/onboarding/quick-picks',
    component: OnboardingQuickPicksScreen,
  },
  {
    path: '/critique',
    component: CritiqueScreen,
  },
  {
    path: '/quick-picks',
    component: QuickPicksScreen,
  },
  {
    path: '/noauth/quick-pick/:critiqueQuestionId',
    component: NoAuthQuickPickScreen,
  },
  {
    path: '/noauth/booking-opportunity/:bookingOpportunityId',
    component: ManageNoauthBookingOpportunityScreen,
  },
  {
    path: '/noauth/download',
    component: NoAuthDownloadScreen,
  },
  {
    path: '/conversations',
    component: ConversationsScreen,
  },
  {
    path: '/conversations/:profileType',
    component: ConversationsScreen,
  },
  {
    path: '/chat/conversation/:conversationId',
    component: ChatScreen,
  },
  {
    path: '/chat/promoter/:currentPromoterId/entertainer/:entertainerId',
    component: ChatScreen,
  },
  {
    path: '/chat/entertainer/:currentEntertainerId/promoter/:promoterId',
    component: ChatScreen,
  },
  {
    path: '/chat/entertainer/:currentEntertainerId/user/:userId',
    component: ChatScreen,
  },
  {
    path: '/chat/user/:currentUserId/entertainer/:entertainerId',
    component: ChatScreen,
  },
  {
    path: '(.*)',
    component: (() => (
      <Page onPageAfterIn={(page) => console.log(page)}>404 Page Not Found</Page>
    )) as FC,
  },
];
